import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { staticImg } from '../utils/media';

export default function Seo({ title, description, image, path, schemaType = 'WebPage', productData }) {
  const { meta, lang } = useI18n();
  const settings = meta?.settings || {};
  const location = useLocation();
  const currentLang = lang || 'tr';
  const siteName = settings.site_title || 'Aegis Studios';
  const finalTitle = title ? `${title} — ${siteName}` : `${siteName} — ${settings.site_description ? settings.site_description.slice(0, 60) : 'Metin2 Solutions'}`;
  const finalDesc = description || settings.site_description || 'Aegis Studios Metin2 Server Files, Sistemler, 3D Modeller, Hosting & VPS paketleri.';
  const url = `https://aegistudios.com${path || location.pathname}`;
  const availableLangs = meta?.available || ['tr', 'en'];

  const rawImage = image || settings.social_og_image || 'site_logo_697de09612208.png';
  const resolvedImg = staticImg(rawImage);
  const finalImage = resolvedImg.startsWith('http') ? resolvedImg : `https://aegistudios.com${resolvedImg.startsWith('/') ? '' : '/'}${resolvedImg}`;

  const faviconFile = settings.site_favicon || 'site_favicon_697de09615bfe.png';
  const faviconUrl = staticImg(faviconFile);

  useEffect(() => {
    document.title = finalTitle;
  }, [finalTitle]);

  // Schema.org structured data for Google Search & Image Indexing
  const schemaOrgJSON = {
    "@context": "https://schema.org",
    "@type": schemaType,
    "name": finalTitle,
    "description": finalDesc,
    "url": url,
    "image": finalImage,
    "publisher": {
      "@type": "Organization",
      "name": siteName,
      "logo": {
        "@type": "ImageObject",
        "url": "https://aegistudios.com/assets/img/site_logo_697de09612208.png"
      }
    }
  };

  if (productData) {
    schemaOrgJSON["@type"] = "Product";
    schemaOrgJSON["offers"] = {
      "@type": "Offer",
      "priceCurrency": "TRY",
      "price": productData.price || "0.00",
      "availability": "https://schema.org/InStock"
    };
  }

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <link rel="icon" type="image/png" href={faviconUrl} />
      <link rel="shortcut icon" href={faviconUrl} />
      <link rel="apple-touch-icon" href={faviconUrl} />
      <link rel="canonical" href={url} />

      {/* Open Graph / Social */}
      <meta property="og:type" content={schemaType === 'Product' ? 'product' : 'website'} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:secure_url" content={finalImage} />
      <meta property="og:locale" content={currentLang === 'tr' ? 'tr_TR' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={finalImage} />

      <link rel="alternate" hrefLang="x-default" href={url} />
      {availableLangs.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={`${url}?lang=${l}`} />
      ))}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgJSON)}
      </script>
    </Helmet>
  );
}
